import { FacilityWasteView } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

import { useWasteFacilityState } from "./WasteFacilityContext";
import { Filter } from "./WasteFacilityFilters";

/** Hook to get the default omnisearch string for the waste facility page line
 * items.
 *
 * `ignoreView` drops the reportability term while keeping the facility, date
 * and tag terms. Callers asking "does this facility have any waste at all"
 * need that: with the view term, a facility whose waste is entirely
 * non-reportable counts zero and the page renders as empty. */
export function useFacilityWasteLineItemsDefaultSearch({ ignoreView = false } = {}) {
  const { tenantId } = useTenant();
  const {
    wasteFacilityState: { deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const { startDate, endDate, view, tags } = deferredFilters as Filter;

  return useMemo(() => {
    const searchParts = [`facilityId:${effectiveWasteFacilityId}`, `tenantId:${tenantId}`];

    // Add date range filters
    if (startDate && endDate) {
      searchParts.push(`shippedDate:${startDate}..${endDate}`);
    } else if (startDate) {
      searchParts.push(`shippedDate:${startDate}`);
    } else if (endDate) {
      searchParts.push(`shippedDate:${endDate}`);
    }

    // Add view filter
    if (!ignoreView && view && view !== FacilityWasteView.All) {
      if (view === FacilityWasteView.NotReportable) {
        searchParts.push(`willNotReport:true`);
      } else if (view === FacilityWasteView.Reportable) {
        searchParts.push(`willNotReport:false`);
      }
    }

    // Add tags filter - quote to preserve spaces, Omnisearch splits by separator inside quotes
    if (tags?.length) {
      const joined = tags.join("⁋");
      searchParts.push(`tags:"${joined}"`);
    }

    return searchParts.join(" ");
  }, [effectiveWasteFacilityId, tenantId, startDate, endDate, view, tags, ignoreView]);
}
